import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { regenerateListingCode } from '@/lib/api';
import { prettifyAmenity } from '@/lib/amenityLabels';
import {
  propertyTypes, statusOptions, landStatusOptions, ownershipTypes,
  amenitiesOptions, landAmenitiesOptions, zoningOptions, roadAccessOptions,
  certificateOptions, generateSlug,
} from './propertyFormConstants';

/**
 * The Add/Edit Property dialog. Extracted from PropertiesManagement.jsx.
 * All form state lives in the parent component (single source of truth) and
 * is passed in via props.
 */
const PropertyFormModal = ({
  showModal,
  setShowModal,
  editingProperty,
  form,
  setForm,
  locations,
  currencyRate,
  cloudinaryEnabled,
  uploading,
  uploadProgress,
  uploadQueue = [],
  preparing = false,
  onPickStart,
  handleImageUpload,
  handleSubmit,
  removeImage,
}) => {
  return (
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-form-modal">
          <DialogHeader>
            <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Internal Property Name <span className="text-red-500">*</span>
                  <span className="text-charcoal-muted text-xs ml-2 font-normal">(admin only — hidden from public site & URL)</span>
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => {
                    // IMPORTANT: do NOT sync slug from the internal name.
                    // The internal name often contains the seller's villa
                    // identifier (e.g. "Villa Sunset Paradise") — leaking it
                    // into the URL would let buyers reach out to the seller
                    // directly. Slug auto-syncs from display_name instead.
                    setForm(prev => ({ ...prev, name: e.target.value }));
                  }}
                  required
                  data-testid="property-name-input"
                  placeholder="e.g., Villa Sunset Paradise"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Public Property Name
                  <span className="text-charcoal-muted text-xs ml-2 font-normal">(shown on website — auto-generated, editable)</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    value={form.display_name || ''}
                    onChange={(e) => {
                      const newDisplay = e.target.value;
                      setForm(prev => {
                        // Auto-sync slug from display_name UNLESS the slug was
                        // already manually customised (i.e. doesn't match the
                        // previous auto-generated value).
                        const prevAutoSlug = generateSlug(prev.display_name || '');
                        const slugIsAuto = !prev.slug || prev.slug === prevAutoSlug;
                        return {
                          ...prev,
                          display_name: newDisplay,
                          slug: slugIsAuto ? generateSlug(newDisplay) : prev.slug,
                        };
                      });
                    }}
                    placeholder="e.g., 4-Bedroom Pool Villa in Canggu"
                    data-testid="property-display-name-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const pool = (form.amenities || []).includes('private_pool') ? 'Pool ' : '';
                      const typeMap = {
                        villa: 'Villa', apartment: 'Apartment', house: 'House',
                        commercial: 'Commercial Space', land: 'Land',
                        townhouse: 'Townhouse', boutique_hotel: 'Boutique Hotel',
                      };
                      const typeLabel = typeMap[form.property_type] || 'Villa';
                      const bedrooms = parseInt(form.bedrooms) || 0;
                      let suggested;
                      if (form.property_type === 'land') suggested = `Land in ${form.location || 'Bali'}`;
                      else if (bedrooms <= 0) suggested = `Studio ${pool}${typeLabel} in ${form.location || 'Bali'}`.replace('  ', ' ');
                      else suggested = `${bedrooms}-Bedroom ${pool}${typeLabel} in ${form.location || 'Bali'}`;
                      setForm(prev => {
                        const prevAutoSlug = generateSlug(prev.display_name || '');
                        const slugIsAuto = !prev.slug || prev.slug === prevAutoSlug;
                        return {
                          ...prev,
                          display_name: suggested,
                          slug: slugIsAuto ? generateSlug(suggested) : prev.slug,
                        };
                      });
                    }}
                    data-testid="property-display-name-regen-btn"
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Listing Code
                  <span className="text-charcoal-muted text-xs ml-2 font-normal">(shown on public site + admin)</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    value={form.listing_code || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, listing_code: e.target.value.toUpperCase() }))}
                    placeholder="Auto-generated on create"
                    className="font-mono"
                    data-testid="property-listing-code-input"
                  />
                  {editingProperty && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        if (!confirm('Generate a new listing code? Existing inquiries already linked to this property will keep referencing the old code.')) return;
                        try {
                          const res = await regenerateListingCode(editingProperty.id);
                          setForm(prev => ({ ...prev, listing_code: res.data.listing_code }));
                          toast.success(`New code: ${res.data.listing_code}`);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to regenerate code');
                        }
                      }}
                      data-testid="property-listing-code-regen-btn"
                    >
                      Regenerate
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  URL Slug
                  <span className="text-charcoal-muted text-xs ml-2 font-normal">(public URL — keep generic, no seller identifiers)</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="e.g., 3-bedroom-villa-in-canggu"
                    data-testid="property-slug-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // Generate slug from display_name + listing_code suffix
                      // so it's unique without leaking the internal name.
                      const base = generateSlug(form.display_name || '');
                      const suffix = (form.listing_code || '').toLowerCase();
                      const next = base
                        ? (suffix ? `${base}-${suffix}` : base)
                        : (suffix || '');
                      setForm(prev => ({ ...prev, slug: next }));
                    }}
                    data-testid="property-slug-regen-btn"
                  >
                    Regenerate
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location *</label>
                <Select value={form.location} onValueChange={(v) => setForm(prev => ({ ...prev, location: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>
            </div>

            {/* Property Details */}
            <div className="grid grid-cols-4 gap-4">
              {form.property_type !== 'land' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bedrooms *</label>
                    <Input
                      type="number"
                      min="0"
                      value={form.bedrooms}
                      onChange={(e) => setForm(prev => ({ ...prev, bedrooms: parseInt(e.target.value) || 0 }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bathrooms *</label>
                    <Input
                      type="number"
                      min="0"
                      value={form.bathrooms}
                      onChange={(e) => setForm(prev => ({ ...prev, bathrooms: parseInt(e.target.value) || 0 }))}
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Land Size (m²){form.property_type === 'land' ? ' *' : ''}
                </label>
                <Input
                  type="number"
                  value={form.land_size}
                  onChange={(e) => setForm(prev => ({ ...prev, land_size: e.target.value }))}
                  required={form.property_type === 'land'}
                />
              </div>
              {form.property_type !== 'land' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Building Size (m²)</label>
                  <Input
                    type="number"
                    value={form.building_size}
                    onChange={(e) => setForm(prev => ({ ...prev, building_size: e.target.value }))}
                  />
                </div>
              )}
            </div>

            {/* For-Sale specifics: Zoning + Certificate of Ownership (+ Road Access for Land) */}
            {form.status === 'for_sale' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Zoning</label>
                  <Select value={form.zoning || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, zoning: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="property-zoning">
                      <SelectValue placeholder="Select zoning" />
                    </SelectTrigger>
                    <SelectContent>
                      {zoningOptions.map(z => (
                        <SelectItem key={z.value || 'none'} value={z.value || 'none'}>{z.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Certificate of Ownership</label>
                  <Select value={form.certificate_of_ownership || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, certificate_of_ownership: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="property-certificate">
                      <SelectValue placeholder="Select certificate" />
                    </SelectTrigger>
                    <SelectContent>
                      {certificateOptions.map(c => (
                        <SelectItem key={c.value || 'none'} value={c.value || 'none'}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.property_type === 'land' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Road Access</label>
                    <Select value={form.road_access || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, road_access: v === 'none' ? '' : v }))}>
                      <SelectTrigger data-testid="land-road-access">
                        <SelectValue placeholder="Select road access" />
                      </SelectTrigger>
                      <SelectContent>
                        {roadAccessOptions.map(r => (
                          <SelectItem key={r.value || 'none'} value={r.value || 'none'}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Pricing & Status */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Status *</label>
                <Select value={form.status} onValueChange={(v) => setForm(prev => ({
                  ...prev,
                  status: v,
                  // Short stays aren't for sale → no ownership type applies
                  ...(v === 'for_rent_short' ? { ownership_type: '' } : {}),
                }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(form.property_type === 'land' ? landStatusOptions : statusOptions).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Price - Required only for For Sale */}
              {form.status === 'for_sale' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Sale Price *</label>
                  <div className="flex gap-2">
                    <Select value={form.price_currency} onValueChange={(v) => setForm(prev => ({ ...prev, price_currency: v }))}>
                      <SelectTrigger className="w-24" data-testid="price-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IDR">IDR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm(prev => ({ ...prev, price: e.target.value }))}
                      required
                      className="flex-1"
                      placeholder={form.price_currency === 'USD' ? 'e.g., 500000' : 'e.g., 8000000000'}
                      data-testid="price-input"
                    />
                  </div>
                  {form.price && (
                    <div className="text-xs text-charcoal-muted mt-1">
                      {form.price_currency === 'IDR'
                        ? `≈ USD ${Math.round(parseFloat(form.price) / currencyRate).toLocaleString()} (live)`
                        : `≈ IDR ${Math.round(parseFloat(form.price) * currencyRate).toLocaleString()} (live)`}
                    </div>
                  )}
                </div>
              )}
              
              {/* Rental Price/Night - Required for Short-term */}
              {form.status === 'for_rent_short' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Rental Price/Night *</label>
                  <div className="flex gap-1">
                    <Select value={form.rental_price_currency} onValueChange={(v) => setForm(prev => ({ ...prev, rental_price_currency: v }))}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IDR">IDR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={form.rental_price}
                      onChange={(e) => setForm(prev => ({ ...prev, rental_price: e.target.value }))}
                      required
                      placeholder={form.rental_price_currency === 'USD' ? 'e.g., 350' : 'e.g., 5500000'}
                      className="flex-1"
                    />
                  </div>
                  {form.rental_price && (
                    <div className="text-xs text-charcoal-muted mt-1">
                      {form.rental_price_currency === 'IDR'
                        ? `≈ USD ${Math.round(parseFloat(form.rental_price) / currencyRate).toLocaleString()}/night (live)`
                        : `≈ IDR ${Math.round(parseFloat(form.rental_price) * currencyRate).toLocaleString()}/night (live)`}
                    </div>
                  )}
                </div>
              )}
              
              {/* Long-term Rental Prices - Required for Long-term */}
              {form.status === 'for_rent_long' && (
                <div className="col-span-3">
                  <label className="block text-sm font-medium mb-1">Rental Price (Monthly or Yearly required) *</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex gap-1">
                        <Select value={form.monthly_rental_price_currency || 'IDR'} onValueChange={(v) => setForm(prev => ({ ...prev, monthly_rental_price_currency: v }))}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IDR">IDR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={form.monthly_rental_price}
                          onChange={(e) => setForm(prev => ({ ...prev, monthly_rental_price: e.target.value }))}
                          placeholder={(form.monthly_rental_price_currency || 'IDR') === 'USD' ? 'Monthly e.g. 3000' : 'Monthly e.g. 45000000'}
                          className="flex-1"
                        />
                      </div>
                      {form.monthly_rental_price && (
                        <div className="text-xs text-charcoal-muted mt-1">
                          {(form.monthly_rental_price_currency || 'IDR') === 'IDR'
                            ? `≈ USD ${Math.round(parseFloat(form.monthly_rental_price) / currencyRate).toLocaleString()}/month (live)`
                            : `≈ IDR ${Math.round(parseFloat(form.monthly_rental_price) * currencyRate).toLocaleString()}/month (live)`}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex gap-1">
                        <Select value={form.yearly_rental_price_currency || 'IDR'} onValueChange={(v) => setForm(prev => ({ ...prev, yearly_rental_price_currency: v }))}>
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IDR">IDR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={form.yearly_rental_price}
                          onChange={(e) => setForm(prev => ({ ...prev, yearly_rental_price: e.target.value }))}
                          placeholder={(form.yearly_rental_price_currency || 'IDR') === 'USD' ? 'Yearly e.g. 30000' : 'Yearly e.g. 450000000'}
                          className="flex-1"
                        />
                      </div>
                      {form.yearly_rental_price && (
                        <div className="text-xs text-charcoal-muted mt-1">
                          {(form.yearly_rental_price_currency || 'IDR') === 'IDR'
                            ? `≈ USD ${Math.round(parseFloat(form.yearly_rental_price) / currencyRate).toLocaleString()}/year (live)`
                            : `≈ IDR ${Math.round(parseFloat(form.yearly_rental_price) * currencyRate).toLocaleString()}/year (live)`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Property Type *</label>
                <Select value={form.property_type} onValueChange={(v) => setForm(prev => ({
                  ...prev,
                  property_type: v,
                  // Land has no bed/bath/building — zero them so they don't carry over from a previous value
                  ...(v === 'land' ? {
                    bedrooms: 0,
                    bathrooms: 0,
                    building_size: '',
                    // Land cannot be a short-term rental (no nightly stays for empty land)
                    status: prev.status === 'for_rent_short' ? 'for_sale' : prev.status,
                  } : {}),
                }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ownership Type - Hidden for short-term stays (not for sale) */}
            {form.status !== 'for_rent_short' && (
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Ownership Type</label>
                  <Select value={form.ownership_type} onValueChange={(v) => setForm(prev => ({ ...prev, ownership_type: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select ownership type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownershipTypes.map(t => (
                        <SelectItem key={t.value || 'none'} value={t.value || 'none'}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Short-term Rental Settings - Show when status is short-term rental or has rental_price */}
            {(form.status === 'for_rent_short' || form.rental_price) && (
              <div className="border border-ubud-jungle/20 rounded-lg p-4 bg-ubud-jungle/5">
                <h3 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-ubud-jungle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Short-term Rental Settings (Nightly)
                </h3>
                <p className="text-xs text-charcoal-muted mb-4">
                  This property will appear on the <strong>Stay</strong> page for short-term bookings.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Guests</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.max_guests}
                      onChange={(e) => setForm(prev => ({ ...prev, max_guests: e.target.value }))}
                      placeholder="e.g., 6"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Min Nights</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.min_nights}
                      onChange={(e) => setForm(prev => ({ ...prev, min_nights: e.target.value }))}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Nights</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.max_nights}
                      onChange={(e) => setForm(prev => ({ ...prev, max_nights: e.target.value }))}
                      placeholder="No limit"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Check-in Time</label>
                    <Input
                      type="time"
                      value={form.check_in_time}
                      onChange={(e) => setForm(prev => ({ ...prev, check_in_time: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Check-out Time</label>
                    <Input
                      type="time"
                      value={form.check_out_time}
                      onChange={(e) => setForm(prev => ({ ...prev, check_out_time: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Leasehold Details - Show when ownership is leasehold or both */}
            {(form.ownership_type === 'leasehold' || form.ownership_type === 'both') && (
              <div className="border border-blue-500/30 rounded-lg p-4 bg-blue-50">
                <h3 className="font-medium text-charcoal mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Leasehold Details
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Years Remaining</label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      value={form.lease_years_remaining}
                      onChange={(e) => {
                        const years = e.target.value;
                        const expiryYear = years ? new Date().getFullYear() + parseInt(years) : '';
                        setForm(prev => ({ 
                          ...prev, 
                          lease_years_remaining: years,
                          lease_expiry_year: expiryYear.toString()
                        }));
                      }}
                      placeholder="e.g., 30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Expiry Year</label>
                    <Input
                      type="number"
                      min={new Date().getFullYear()}
                      max={new Date().getFullYear() + 100}
                      value={form.lease_expiry_year}
                      onChange={(e) => setForm(prev => ({ ...prev, lease_expiry_year: e.target.value }))}
                      placeholder="e.g., 2055"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="lease_extendable"
                      checked={form.lease_extendable}
                      onCheckedChange={(checked) => setForm(prev => ({ ...prev, lease_extendable: checked }))}
                    />
                    <label htmlFor="lease_extendable" className="text-sm cursor-pointer">Lease Extendable</label>
                  </div>
                </div>
                {form.lease_years_remaining && (
                  <div className="mt-3 p-2 bg-white rounded text-sm text-charcoal-muted">
                    📋 Lease: <strong>{form.lease_years_remaining} years</strong> remaining 
                    {form.lease_expiry_year && <span> (expires {form.lease_expiry_year})</span>}
                    {form.lease_extendable && <span className="text-green-600 ml-2">• Extendable</span>}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Short Description *</label>
              <Textarea
                value={form.short_description}
                onChange={(e) => setForm(prev => ({ ...prev, short_description: e.target.value }))}
                required
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Full Description *</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                required
                rows={4}
              />
            </div>

            {/* Amenities */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {form.property_type === 'land' ? 'Land Features' : 'Amenities'}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(form.property_type === 'land' ? landAmenitiesOptions : amenitiesOptions).map(amenity => (
                  <label key={amenity} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.amenities.includes(amenity)}
                      onCheckedChange={(checked) => {
                        setForm(prev => ({
                          ...prev,
                          amenities: checked
                            ? [...prev.amenities, amenity]
                            : prev.amenities.filter(a => a !== amenity),
                        }));
                      }}
                    />
                    <span className="text-sm">{prettifyAmenity(amenity)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                Images
                {cloudinaryEnabled && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" clipRule="evenodd" />
                    </svg>
                    Cloud Storage
                  </span>
                )}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onClick={() => { if (onPickStart) onPickStart(); }}
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="image-upload"
                  className={`flex flex-col items-center ${uploading || preparing ? 'cursor-wait' : 'cursor-pointer'}`}
                >
                  <Upload className="w-8 h-8 text-charcoal-muted mb-2" />
                  <span className="text-sm text-charcoal-muted" data-testid="upload-status-text">
                    {preparing
                      ? 'Preparing files… (this can take a moment for iCloud / Files photos)'
                      : uploading
                        ? `Uploading… ${uploadProgress}%`
                        : 'Click to upload images (you can select multiple)'}
                  </span>
                  {!uploading && !preparing && (
                    <span className="text-[11px] text-charcoal-muted/70 mt-1">
                      Photos are auto-compressed to ~400KB for faster upload.
                    </span>
                  )}
                  {(preparing || uploading) && (
                    <div className="w-full mt-2 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`bg-ubud-jungle h-2 rounded-full transition-all duration-300 ${preparing ? 'animate-pulse w-1/3' : ''}`}
                        style={preparing ? undefined : { width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                </label>
              </div>
              {!uploading && !preparing && (
                <div className="mt-2 text-[11px] text-charcoal-muted/80 bg-warm-sand/40 border border-warm-sand rounded-lg px-3 py-2 leading-relaxed" data-testid="upload-hint">
                  <span className="font-medium text-charcoal">Tip for iPad / iPhone:</span> tap{' '}
                  <span className="font-medium">Photo Library</span> → &ldquo;Select&rdquo; → pick multiple → &ldquo;Done&rdquo; (fastest).{' '}
                  <span className="text-charcoal-muted">Google Drive / iCloud via &ldquo;Files&rdquo; works too, but iOS only lets you pick one photo at a time and needs to download each one first — save to your Photo Library first for batch uploads.</span>
                </div>
              )}

              {/* Per-file upload queue */}
              {uploading && uploadQueue.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto bg-warm-sand/50 rounded-lg p-2 space-y-1" data-testid="upload-queue">
                  {uploadQueue.map((item, idx) => (
                    <div key={`${item.name}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-charcoal max-w-[60%]" title={item.name}>{item.name}</span>
                      {item.status === 'pending' && (
                        <span className="text-charcoal-muted">Queued…</span>
                      )}
                      {item.status === 'compressing' && (
                        <span className="text-amber-600 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                          Compressing…
                        </span>
                      )}
                      {item.status === 'uploading' && (
                        <span className="text-ubud-jungle flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-ubud-jungle animate-pulse"></span>
                          Uploading…
                        </span>
                      )}
                      {item.status === 'success' && (
                        <span className="text-green-600">✓ Done</span>
                      )}
                      {item.status === 'error' && (
                        <span className="text-red-600 truncate max-w-[40%]" title={item.error}>✕ Failed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {form.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {form.images.map((img, idx) => (
                    <div key={`${img}-${idx}`} className="relative group">
                      <img src={img} alt="" className="w-full h-20 object-cover rounded-lg" />
                      {form.cover_image === img && (
                        <span className="absolute top-1 left-1 bg-ubud-jungle text-white text-xs px-1 rounded">Cover</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {form.cover_image !== img && (
                        <button
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, cover_image: img }))}
                          className="absolute bottom-1 left-1 bg-white text-charcoal text-xs px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Set Cover
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Latitude</label>
                <Input
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) => setForm(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="-8.6478"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Longitude</label>
                <Input
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) => setForm(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="115.1385"
                />
              </div>
            </div>

            {/* Featured */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="featured"
                checked={form.featured}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, featured: checked }))}
              />
              <label htmlFor="featured" className="text-sm cursor-pointer">Feature this property on homepage</label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-ubud-jungle hover:bg-ubud-jungle-dark text-white" data-testid="save-property-btn">
                {editingProperty ? 'Update Property' : 'Create Property'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
  );
};

export default PropertyFormModal;
